import C from "@/components/sidebar/StandardSidebar.module.css";

import { localeCompareBy } from "@/components/data/comparers.ts";
import { getFilteredItems } from "@/components/data/getFilteredItems.ts";
import { getSortedItems, type SimpleSortField } from "@/components/data/getSortedItems.ts";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { EmptyItem } from "@/components/sidebar/EmptyItem.tsx";
import { PriceBlock } from "@/components/sidebar/PriceBlock.tsx";
import { ListResult, SidebarList, type SidebarListState } from "@/components/sidebar/SidebarList.tsx";
import { StandardBlock } from "@/components/sidebar/StandardSidebar.tsx";
import { ResourceCreateLinks } from "@/modules/api/resourceCreateLinks.ts";
import { allDiskWithPricesQueryAtom, type DiskWithPrice } from "@/modules/disks/disksListQuery.ts";
import { diskLink } from "@/modules/disks/links.ts";
import { hoursPerMonth } from "@/modules/pricing/hourlyToMonthly.ts";
import { cn } from "@/utils/classNames.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { apiDatetimeToDate } from "@warrenio/api-spec/conversion";
import { useAtomValue } from "jotai/react";

const sortFields = [
    {
        id: "name",
        title: <Trans>Name</Trans>,
        compare: localeCompareBy((i) => i.display_name ?? ""),
    },
    {
        id: "created_at",
        title: <Trans>Created date</Trans>,
        getValue: (i) => apiDatetimeToDate(i.created_at).getTime(),
    },
    { id: "size_gb", title: <Trans>Size</Trans>, getValue: (i) => i.size_gb },
    { id: "location", title: <Trans>Location</Trans>, getValue: (i) => i.location },
    { id: "price", title: <Trans>Monthly cost</Trans>, getValue: (i) => i.price.hourly * hoursPerMonth },
] satisfies SimpleSortField<DiskWithPrice>[];

export default function DiskList() {
    const { t } = useLingui();
    return (
        <SidebarList
            data={(props) => <DiskData {...props} />}
            title={t`Disks`}
            sortFields={sortFields}
            actionButton={{
                title: t`Create new`,
                action: ResourceCreateLinks.disk,
            }}
            listId="disk"
        />
    );
}

export function DiskData({ queryString, sortId, sortAsc }: SidebarListState) {
    const query = useAtomValue(allDiskWithPricesQueryAtom);

    let diskItems = getFilteredItems(queryString, [...(query.data?.values() ?? [])], (item) => [item.uuid]);
    diskItems = getSortedItems(sortFields, { sortId, sortAsc }, diskItems);

    return (
        <ListResult
            items={diskItems.map((obj) => (
                <DiskBlock key={obj.uuid} item={obj} />
            ))}
            isSearching={!!queryString}
            query={query}
            emptyItem={
                <EmptyItem icon="jp-stack-icon">
                    <Trans>Create a new disk and start storing your data.</Trans>
                </EmptyItem>
            }
        />
    );
}

export function DiskBlock({ item }: { item: DiskWithPrice }) {
    const { uuid, size_gb, display_name, attached_to, location, status, billing_account_id } = item;

    return (
        <StandardBlock link={diskLink(item)}>
            <div className={C.StandardBlockIcon}>
                <MaskIcon className="jp-ssd-disk-icon size-1rem" />
            </div>

            <div className={cn(C.StandardBlockName, "text-ellipsis")}>
                <b className="text-ellipsis">
                    {display_name ? display_name : attached_to?.name ? <i>{attached_to?.name}</i> : uuid}
                </b>
                <div className="text-ellipsis text-primary">
                    {size_gb} <Trans>GB</Trans> / {location}
                </div>
            </div>

            <div className={C.StandardBlockType}>
                {status === "Reserved" ? (
                    <div className="text-error text-uppercase">
                        <Trans>Reserved</Trans>
                    </div>
                ) : attached_to ? (
                    <div className="text-success text-uppercase">
                        <Trans>Attached</Trans>
                    </div>
                ) : (
                    <div className="text-error text-uppercase">
                        <Trans>Detached</Trans>
                    </div>
                )}
            </div>

            <PriceBlock price={item.price} billingAccountId={billing_account_id} />
        </StandardBlock>
    );
}
